import { LOCKED_TEAM_ADVISORS } from '@src/components/sco/Advisors/model/access'
import { founderUserIdOf } from '@src/components/sco/Advisors/utils/mapFounderEngagements'
import { ADVISOR_ENGAGEMENT_SOURCE_TEAM } from '@src/constants/advisor-engagement-sources'
import { ScoAdvisorEngagement } from '@src/graphql-types'

export type LockedTeamAdvisor = (typeof LOCKED_TEAM_ADVISORS)[number]

/**
 * One row in the Advisors inbox.
 *
 * Extracted from the old `AdvisorsConversationsNavList` sidebar. The sidebar's
 * windowing (top-N slice, pin-outside-window, per-side overflow menus) is
 * deliberately **not** carried over — the inbox list scrolls the full roster.
 */
export type DirectInboxAdvisor = {
  userId: string
  name: string
  pictureUrl?: string | null
}

export type ConversationRow =
  | { kind: 'board'; engagement: ScoAdvisorEngagement; sortMs: number; unread: boolean; isAI: boolean }
  | { kind: 'founder'; engagement: ScoAdvisorEngagement; sortMs: number; unread: boolean; isAI: false }
  | { kind: 'locked'; advisor: LockedTeamAdvisor; sortMs: number; unread: false; isAI: false }
  | {
      kind: 'direct'
      userId: string
      name: string
      pictureUrl?: string | null
      sortMs: number
      unread: boolean
      isAI: false
    }

export type ConversationRowKind = ConversationRow['kind']

/** Last chat activity in ms, from the thread or the denormalized column. */
export const chatLastActiveMs = (engagement: ScoAdvisorEngagement): number => {
  const raw = engagement.chatThread?.lastActiveAt || engagement.chatLastActiveAt
  if (!raw) {
    return 0
  }
  const ms = new Date(raw).getTime()
  return Number.isFinite(ms) ? ms : 0
}

export const hasUnread = (engagement: ScoAdvisorEngagement): boolean =>
  (engagement.chatThread?.currentUserUnreadCommentsCount ?? 0) > 0

export const boardRow = (engagement: ScoAdvisorEngagement): ConversationRow => ({
  kind: 'board',
  engagement,
  sortMs: chatLastActiveMs(engagement),
  unread: hasUnread(engagement),
  isAI: !!engagement.isAI,
})

export const founderRow = (engagement: ScoAdvisorEngagement): ConversationRow => ({
  kind: 'founder',
  engagement,
  sortMs: chatLastActiveMs(engagement),
  unread: hasUnread(engagement),
  isAI: false,
})

export const lockedRow = (advisor: LockedTeamAdvisor): ConversationRow => ({
  kind: 'locked',
  advisor,
  sortMs: 0,
  unread: false,
  isAI: false,
})

/** Open DM with a marketplace advisor who is not (yet) on a board seat. */
export const directRow = (advisor: DirectInboxAdvisor): ConversationRow => ({
  kind: 'direct',
  userId: advisor.userId,
  name: advisor.name,
  pictureUrl: advisor.pictureUrl,
  sortMs: 0,
  unread: false,
  isAI: false,
})

/** The person a row is about — advisor for board/locked rows, founder for founder rows. */
export const conversationSubjectUserId = (row: ConversationRow): string | null => {
  if (row.kind === 'board') {
    return row.engagement.advisorUserId || row.engagement.advisorUser?.userId || null
  }
  if (row.kind === 'founder') {
    return founderUserIdOf(row.engagement)
  }
  if (row.kind === 'direct') {
    return row.userId
  }
  return row.advisor.userId || null
}

/** Stable per-row key for React lists and selection comparisons. */
export const conversationRowKey = (row: ConversationRow): string => {
  if (row.kind === 'locked') {
    return `locked:${row.advisor.navKey}`
  }
  if (row.kind === 'direct') {
    return `direct:${row.userId}`
  }
  const id = row.engagement.advisorEngagementId || conversationSubjectUserId(row) || ''
  return `${row.kind}:${id}`
}

/** Unread first, then most recent activity. Wil ordering is handled by `sortConversationRows`. */
export const compareConversationRows = (a: ConversationRow, b: ConversationRow): number => {
  if (a.unread !== b.unread) {
    return a.unread ? -1 : 1
  }
  return b.sortMs - a.sortMs
}

/**
 * One row per subject user; keeps the first occurrence.
 *
 * A user can appear as both a board advisor and a founder (mutual advising), and
 * the locked roster overlaps real board seats once a user upgrades.
 */
export const dedupeConversationRows = (rows: ConversationRow[]): ConversationRow[] => {
  const seen = new Set<string>()
  const deduped: ConversationRow[] = []

  for (const row of rows) {
    const userId = conversationSubjectUserId(row)
    if (!userId) {
      deduped.push(row)
      continue
    }
    if (seen.has(userId)) {
      continue
    }
    seen.add(userId)
    deduped.push(row)
  }

  return deduped
}

/**
 * Inbox order: Wil (AI) pinned first, then unread, then recency.
 *
 * Locked upsell rows always sort last — they are merchandising, not conversations,
 * and should never outrank a real thread.
 */
export const sortConversationRows = (rows: ConversationRow[]): ConversationRow[] => {
  const ai = rows.filter(row => row.isAI)
  const locked = rows.filter(row => row.kind === 'locked')
  const rest = rows.filter(row => !row.isAI && row.kind !== 'locked').sort(compareConversationRows)
  return [...ai, ...rest, ...locked]
}

/**
 * A row the user can actually talk in — not Wil (link-out), not a locked $299
 * merchandising row, not a team advisor behind the paywall.
 */
export const isRealInboxThread = (row: ConversationRow, canAccessAdvisorTeam: boolean): boolean => {
  if (row.kind === 'locked') {
    return false
  }
  if (row.kind === 'direct') {
    return true
  }
  if (row.kind === 'board' && row.engagement.isAI) {
    return false
  }
  if (
    row.kind === 'board' &&
    row.engagement.sourceKey === ADVISOR_ENGAGEMENT_SOURCE_TEAM &&
    !canAccessAdvisorTeam
  ) {
    return false
  }
  return true
}

/** Unread real human first, then any real thread. Never a paywall row. */
export const pickDefaultInboxRow = (
  rows: ConversationRow[],
  canAccessAdvisorTeam: boolean
): ConversationRow | null => {
  const real = rows.filter(row => isRealInboxThread(row, canAccessAdvisorTeam))
  return real.find(row => row.unread) ?? real[0] ?? null
}

/** Build the full inbox list from the board, founder engagements and locked roster. */
export const buildConversationRows = ({
  boardEngagements,
  founderEngagements,
  lockedAdvisors = [],
  directAdvisors = [],
}: {
  boardEngagements: ScoAdvisorEngagement[]
  founderEngagements: ScoAdvisorEngagement[]
  lockedAdvisors?: LockedTeamAdvisor[]
  directAdvisors?: DirectInboxAdvisor[]
}): ConversationRow[] =>
  dedupeConversationRows(
    sortConversationRows([
      ...boardEngagements.map(boardRow),
      ...founderEngagements.map(founderRow),
      ...directAdvisors.map(directRow),
      ...lockedAdvisors.map(lockedRow),
    ])
  )
